import clsx from 'clsx';

/**
 * O conjunto de ícones da navegação e das acções.
 *
 * Deliberadamente separado do léxico (`EvidenceGlyph`, `StatusMark`): aqueles
 * **codificam** — tipo de evidência, estado epistemológico — e são testados
 * para continuarem distinguíveis sem cor. Estes só apontam para um sítio da
 * app. Misturá-los na mesma pasta acabaria com alguém a usar um ícone de menu
 * para dizer "manuscrito", e a distinção que o produto inteiro assenta em
 * perder-se-ia num ficheiro de ícones.
 *
 * Traço e não preenchimento, 1.6px, cantos redondos: é o que combina com o
 * léxico geométrico sem competir com ele.
 */
const PATHS: Record<string, string> = {
  // navegação
  explore: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M15.5 8.5l-2 5-5 2 2-5z',
  corpus: 'M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2 2 2 0 0 1 2-2h4.5A1.5 1.5 0 0 1 20 5.5v11a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 0 0-2 2 2 2 0 0 0-2-2H5.5A1.5 1.5 0 0 1 4 16.5zM12 6v14',
  compare: 'M12 3v18M4 7h5M4 12h5M4 17h5M15 7h5M15 12h5M15 17h5',
  play: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M10 8.5l6 3.5-6 3.5z',
  learn: 'M12 4 2.5 9 12 14l9.5-5zM6.5 11.2V16c0 1.4 2.5 2.6 5.5 2.6s5.5-1.2 5.5-2.6v-4.8',
  figure: 'M12 4a3.4 3.4 0 1 0 0 6.8A3.4 3.4 0 0 0 12 4M5 20c0-3.6 3.1-5.6 7-5.6s7 2 7 5.6',
  // acções e estado
  support: 'M12 20s-7-4.4-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7-2.8c1.6 1.7 1 4.6-1 6.8z',
  methodology: 'M12 4v16M7 8h10M6.5 8 4 14h5zM17.5 8 15 14h5zM8 20h8',
  me: 'M12 4a3.6 3.6 0 1 0 0 7.2A3.6 3.6 0 0 0 12 4M4.5 20a7.5 7.5 0 0 1 15 0',
  menu: 'M4 7h16M4 12h16M4 17h16',
  // inventário de uma figura
  prophecy: 'M12 3.5 14.1 9h5.4l-4.4 3.4 1.7 5.6L12 14.6 7.2 18l1.7-5.6L4.5 9h5.4z',
  identity: 'M12 3.5 19.5 6v5.5c0 4-3.1 7.4-7.5 9-4.4-1.6-7.5-5-7.5-9V6zM9.2 12l2 2 3.6-3.8',
  passage: 'M7 4h10a1 1 0 0 1 1 1v15l-6-3.4L6 20V5a1 1 0 0 1 1-1',
  theme: 'M4 5.5h7v7H4zM13 5.5h7v7h-7zM4 14.5h7v4H4zM13 14.5h7v4h-7z',
  episode: 'M5 6.5h14M5 12h14M5 17.5h9',
  bold: 'M6 20V5h7.5a3.6 3.6 0 0 1 0 7.2H6M13.5 12.2H15a3.9 3.9 0 0 1 0 7.8H6',
  objection: 'M12 4 3 19.5h18zM12 10v4.2M12 16.8v.2',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13M15.2 15.2 20 20',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
};

export type IconName = keyof typeof PATHS;

export function Icon({
  name, size = 18, className,
}: {
  readonly name: IconName;
  readonly size?: number;
  readonly className?: string;
}) {
  const d = PATHS[name];
  if (d === undefined) return null;
  return (
    <svg
      className={clsx('et-icon', className)}
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={1.6}
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
    >
      <path d={d} />
    </svg>
  );
}
